import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@shared/supabaseClient';
import { getUserProfile } from '@shared/services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null); // Raw Supabase auth user
  const profileCacheKey = 'last_user_profile';
  const currentAuthIdRef = useRef(null);
  const profileLoadingRef = useRef(false);

  // Effect 1: Auth listener + initial session check.
  //
  // IMPORTANT: Supabase's _initialize() runs inside navigator.locks.
  // During init it fires SIGNED_IN before INITIAL_SESSION. If we set authUser
  // from that premature SIGNED_IN, React's useEffect runs the profile fetch
  // in the microtask gap — but getUserProfile calls supabase REST which needs
  // getSession() which needs the SAME lock → deadlock.
  //
  // Solution: ignore all events until initial load is done via getSession(),
  // which internally awaits _initialize() completion (lock released).
  useEffect(() => {
    let cancelled = false;
    let initialLoadDone = false;

    // getSession() waits for Supabase init to finish (lock released),
    // so the REST calls in profile loading won't deadlock.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      initialLoadDone = true;
      if (session?.user) {
        setAuthUser(session.user);
      } else {
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    // Listen for subsequent auth changes (sign-in, sign-out).
    // During initialization, events are skipped (handled by getSession above).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      // Always process sign-out immediately
      if (event === 'SIGNED_OUT') {
        currentAuthIdRef.current = null;
        setAuthUser(null);
        setUser(null);
        setLoading(false);
        return;
      }

      // Skip events during initialization — getSession handles first load
      if (!initialLoadDone) return;

      // TOKEN_REFRESHED: JWT refreshed, user data unchanged
      if (event === 'TOKEN_REFRESHED') return;

      // SIGNED_IN after initial load = genuine new login from login form
      if (event === 'SIGNED_IN' && session?.user) {
        if (currentAuthIdRef.current !== session.user.id) {
          setAuthUser(session.user);
        }
      }
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  // Effect 2: Load the user profile whenever authUser changes.
  // This runs OUTSIDE the auth lock, so Supabase REST calls work normally.
  useEffect(() => {
    if (!authUser) return;

    // Already loaded this user
    if (currentAuthIdRef.current === authUser.id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      if (profileLoadingRef.current) return;
      profileLoadingRef.current = true;

      try {
        const profile = await getUserProfile(authUser.id, authUser.email);

        if (cancelled) return;

        if (!profile) {
          console.warn('⚠️ No user profile found for auth_id:', authUser.id);
          setUser(null);
          setLoading(false);
          return;
        }

        try {
          localStorage.setItem(profileCacheKey, JSON.stringify(profile));
        } catch (e) { /* storage full — non-critical */ }
        currentAuthIdRef.current = authUser.id;
        setUser(profile);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('❌ Error loading user profile:', error);

        // Try localStorage cache as fallback for network failures
        try {
          const cached = localStorage.getItem(profileCacheKey);
          const cachedProfile = cached ? JSON.parse(cached) : null;
          if (cachedProfile?.authId === authUser.id) {
            console.warn('⚠️ Using cached profile (network error).');
            currentAuthIdRef.current = authUser.id;
            setUser(cachedProfile);
            setLoading(false);
            return;
          }
        } catch (e) { /* corrupt cache — ignore */ }

        setUser(null);
        setLoading(false);
      } finally {
        profileLoadingRef.current = false;
      }
    };

    loadProfile();

    return () => { cancelled = true; };
  }, [authUser]);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      setUser(null);
      // Don't use localStorage.clear() - it removes company logo and other settings!
      // Only clear Supabase auth tokens if needed (Supabase handles this automatically)
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
